import { Play, Volume2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef, useState, useEffect, useCallback } from 'react';

// Move weddings data outside component to prevent recreation on every render
const WEDDINGS_DATA = [
  {
    youtubeId: 'mK51pqrzSnI',
    title: 'Vyshnavie & Anirudh',
    thumbnail: 'https://img.youtube.com/vi/mK51pqrzSnI/maxresdefault.jpg'
  },
  {
    youtubeId: 'NMs7L2KurzA',
    title: 'Vishnavi with Benny',
    thumbnail: 'https://img.youtube.com/vi/NMs7L2KurzA/maxresdefault.jpg'
  },
  {
    youtubeId: 'ZjSOz64stnY',
    title: 'Akash Surya',
    thumbnail: 'https://img.youtube.com/vi/ZjSOz64stnY/maxresdefault.jpg'
  },
  {
    youtubeId: 'zGlt52WgUl8',
    title: 'Amritha & Phani',
    thumbnail: 'https://img.youtube.com/vi/zGlt52WgUl8/maxresdefault.jpg'
  },
  {
    youtubeId: 'E8kbVPekzsw',
    title: 'Susila - Siddharth',
    thumbnail: 'https://img.youtube.com/vi/E8kbVPekzsw/maxresdefault.jpg'
  },
  {
    youtubeId: 'p-fIW9Rm9NE',
    title: 'Suhas - Monika',
    thumbnail: 'https://img.youtube.com/vi/p-fIW9Rm9NE/maxresdefault.jpg'
  },
  {
    youtubeId: 'qGPuRmiqvMA',
    title: 'Josh - Daisy',
    thumbnail: 'https://img.youtube.com/vi/qGPuRmiqvMA/maxresdefault.jpg'
  },
  {
    youtubeId: 'qWLIKUt9EUc',
    title: 'ishwarya - mithin',
    thumbnail: 'https://img.youtube.com/vi/qWLIKUt9EUc/maxresdefault.jpg'
  },
  {
    youtubeId: 'B0UMZGhPPxg',
    title: 'tejitha - kc',
    thumbnail: 'https://img.youtube.com/vi/B0UMZGhPPxg/maxresdefault.jpg'
  },
  {
    youtubeId: 'mhez6qPLINo',
    title: 'kriti - asshray',
    thumbnail: 'https://img.youtube.com/vi/mhez6qPLINo/maxresdefault.jpg'
  },
  {
    youtubeId: 'kutvLF9m8y4',
    title: 'Nikita - Prateek',
    thumbnail: 'https://img.youtube.com/vi/kutvLF9m8y4/maxresdefault.jpg'
  },
];

const WeddingsSection = () => {
  const scrollContainerRef = useRef(null);
  const playersRef = useRef([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [currentScroll, setCurrentScroll] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Use the stable constant
  const weddings = WEDDINGS_DATA;

  const initializePlayers = useCallback(() => {
    const newPlayers = weddings.map((wedding, i) => {
      return new window.YT.Player(`youtube-player-${i}`, {
        videoId: wedding.youtubeId,
        playerVars: {
          autoplay: 1,
          controls: 0,
          mute: 1,
          loop: 1,
          playlist: wedding.youtubeId,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
        },
      });
    });
    playersRef.current = newPlayers;
  }, [weddings]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = initializePlayers;
    } else {
      initializePlayers();
    }

    // Cleanup on unmount
    return () => {
      playersRef.current.forEach(player => {
        if (player && player.destroy) {
          player.destroy();
        }
      });
    };
  }, [initializePlayers]);

  // Handle scroll tracking
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScroll = () => {
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const max = scrollWidth - clientWidth;

      setCurrentScroll(scrollLeft);
      setMaxScroll(max);
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < max - 10);

      if (scrollLeft >= max - 10) {
        setTimeout(() => {
          const nextSection = container.closest('section').nextElementSibling;
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 800);
      }
    };

    container.addEventListener('scroll', updateScroll);
    setTimeout(updateScroll, 100);

    return () => container.removeEventListener('scroll', updateScroll);
  }, []);

  const handleVideoClick = (index) => {
    const players = playersRef.current;
    const player = players[index];
    if (!player || !player.getPlayerState) return;

    if (activeIndex === index) {
      player.mute();
      setActiveIndex(null);
      return;
    }

    // Mute all others
    players.forEach((p) => {
      if (p && p.mute) {
        p.mute();
      }
    });

    // Unmute clicked video
    player.unMute();
    player.setVolume(100);
    player.playVideo();
    setActiveIndex(index);
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.85;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <section id="weddings" className="bg-neutral-950 py-12 sm:py-16 lg:py-22 overflow-hidden relative">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Curved Arrow Indicator - Desktop */}
        <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 opacity-20">
          <svg width="120" height="80" viewBox="0 0 120 80" fill="none">
            <path
              d="M10 40 Q 40 20, 70 40 T 110 40"
              stroke="#d4af37"
              strokeWidth="1.5"
              fill="none"
              strokeDasharray="4 4"
            />
            <path
              d="M 100 35 L 110 40 L 100 45"
              stroke="#d4af37"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="text-[#d4af37]/40 text-[10px] tracking-[0.3em] mt-2 text-center">
            SCROLL
          </p>
        </div>

        {/* Swipe Indicator - Mobile */}
        <div className="lg:hidden absolute right-4 top-1/2 -translate-y-1/2 opacity-30 animate-pulse">
          <svg width="60" height="40" viewBox="0 0 60 40" fill="none">
            <path
              d="M5 20 Q 20 10, 35 20 T 55 20"
              stroke="#d4af37"
              strokeWidth="1"
              fill="none"
              strokeDasharray="3 3"
            />
            <path
              d="M 48 16 L 55 20 L 48 24"
              stroke="#d4af37"
              strokeWidth="1"
              fill="none"
            />
          </svg>
          <p className="text-[#d4af37]/40 text-[8px] tracking-widest mt-1 text-center">
            SWIPE
          </p>
        </div>

        {/* Gradient Fade Effects */}
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-neutral-950 to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-neutral-950 to-transparent z-10" />
      </div>

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 sm:gap-6 mb-8 sm:mb-10">
          <div>
            <span className="text-[#d4af37] text-[10px] tracking-[0.5em] block mb-2">
              FOREVER MOMENTS
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl text-white font-serif font-light">
              Wedding <span className="italic text-neutral-500">Films</span>
            </h2>
          </div>

          <p className="text-neutral-500 text-xs sm:text-sm leading-relaxed max-w-xs sm:max-w-md sm:text-right">
            Capturing timeless love stories with elegance, emotion,
            and cinematic artistry that lasts forever.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons - Desktop Only */}
          <button
            onClick={() => scroll('left')}
            className={`hidden lg:flex absolute -left-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full 
                bg-neutral-900/90 backdrop-blur-sm border border-[#d4af37]/40 
                items-center justify-center transition-all duration-300
                ${canScrollLeft ? 'opacity-100 hover:bg-[#d4af37]/20 hover:scale-110' : 'opacity-0 pointer-events-none'}`}
          >
            <ChevronLeft className="w-6 h-6 text-[#d4af37]" />
          </button>

          <button
            onClick={() => scroll('right')}
            className={`hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full 
                bg-neutral-900/90 backdrop-blur-sm border border-[#d4af37]/40 
                items-center justify-center transition-all duration-300
                ${canScrollRight ? 'opacity-100 hover:bg-[#d4af37]/20 hover:scale-110' : 'opacity-0 pointer-events-none'}`}
          >
            <ChevronRight className="w-6 h-6 text-[#d4af37]" />
          </button>

          {/* Horizontal Scroll Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 sm:gap-5 lg:gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-x'
            }}
          >
            {weddings.map((wedding, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-[60vw] lg:w-[45vw] xl:w-[35vw] 
                    cursor-pointer relative group"
                style={{ scrollSnapAlign: 'center' }}
                onClick={() => handleVideoClick(i)}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Video Container - 16:9 Aspect Ratio */}
                <div className="aspect-video overflow-hidden relative rounded-sm bg-neutral-900">
                  {/* YouTube Player */}
                  <div
                    id={`youtube-player-${i}`}
                    className={`w-full h-full transition-all duration-700 
                        ${hoveredIndex === i ? 'scale-105' : 'scale-100'}`}
                    style={{
                      pointerEvents: 'none',
                      filter: activeIndex === i ? 'brightness(1)' : 'brightness(0.75)'
                    }}
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/70 via-transparent to-neutral-950/30 opacity-60 pointer-events-none" />

                  {/* Top Index Badge */}
                  <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                    <span className="text-[#d4af37]/60 text-[10px] sm:text-xs tracking-widest font-mono">
                      0{i + 1}
                    </span>
                  </div>

                  {/* Sound Indicator */}
                  <div className={`absolute top-3 sm:top-4 right-3 sm:right-4 transition-opacity duration-300 
                      ${activeIndex === i ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="flex items-center gap-2 bg-[#d4af37]/20 backdrop-blur-sm px-2 py-1 rounded-full border border-[#d4af37]/30">
                      <Volume2 className="w-3 h-3 sm:w-4 sm:h-4 text-[#d4af37]" />
                      <span className="text-[#d4af37] text-[9px] sm:text-[10px] tracking-wider">ON</span>
                    </div>
                  </div>

                  {/* Center Play Icon */}
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300
                      ${hoveredIndex === i && activeIndex !== i ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-white/50 flex items-center justify-center 
                        backdrop-blur-md bg-neutral-950/30">
                      <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-1" />
                    </div>
                  </div>

                  {/* Bottom Title Bar */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-neutral-950/90 to-transparent">
                    <span className={`text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.2em] transition-colors duration-300 font-light
                        ${activeIndex === i ? 'text-[#d4af37]' : 'text-white/80'}`}>
                      {wedding.title.toUpperCase()}
                    </span>
                  </div>

                  {/* Active Border */}
                  <div className={`absolute inset-0 border-2 transition-all duration-500 rounded-sm pointer-events-none
                      ${activeIndex === i ? 'border-[#d4af37]/60 shadow-lg shadow-[#d4af37]/20' : 'border-transparent'}`} />
                </div>
              </div>
            ))}
          </div>

          {/* Scroll Progress Bar */}
          <div className="mt-6 sm:mt-8 h-0.5 bg-neutral-800/50 rounded-full overflow-hidden mx-auto max-w-xl">
            <div
              className="h-full bg-gradient-to-r from-[#d4af37]/50 via-[#d4af37] to-[#d4af37]/50 transition-all duration-300 rounded-full"
              style={{
                width: maxScroll > 0 ? `${(currentScroll / maxScroll) * 100}%` : '0%'
              }}
            />
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {weddings.map((_, i) => {
              const cardWidth = scrollContainerRef.current?.firstChild?.offsetWidth || 0;
              const isActive = currentScroll >= (cardWidth * i) - (cardWidth / 2) &&
                currentScroll < (cardWidth * (i + 1)) - (cardWidth / 2);
              return (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 
                      ${isActive ? 'w-8 bg-[#d4af37]' : 'w-1 bg-neutral-700'}`}
                />
              );
            })}
          </div>
        </div>

      </div>

      <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
    </section>
  );
};

export default WeddingsSection;